
import { useEffect, useState } from 'react';
import { 
  initializeClientCache, 
  cleanupClientCache, 
  clearAllCaches, 
  getCacheMetrics,
  getAllCacheMetrics,
  getCacheHitRate,
  optimizeClientCache,
  getCacheOptimizationRecommendations,
  getCacheSizeMetrics,
  invalidateCacheByTopic,
  invalidateCacheByLanguage,
  invalidateCacheByKnowledgeUpdate,
  cacheEventBus
} from '@/services/cache/client-cache-service';
import { setupMemoryMonitoring } from '@/utils/deviceUtils';
import type { DeviceCapabilities } from '@/services/cache/device-detection';
import type { CacheMetrics } from '@/services/cache/cache-metrics';

export type { CacheMetrics, DeviceCapabilities };

export const useClientCache = () => {
  const [isInitialized, setIsInitialized] = useState(false);
  const [metrics, setMetrics] = useState<Record<string, CacheMetrics>>({});
  const [isLoadingMetrics, setIsLoadingMetrics] = useState(false);
  const [recommendations, setRecommendations] = useState<string[]>([]);
  const [sizeMetrics, setSizeMetrics] = useState<Record<string, number>>({});
  const [deviceCapabilities, setDeviceCapabilities] = useState<DeviceCapabilities | null>(null);
  const [cacheEvents, setCacheEvents] = useState<{event: string, count: number}[]>([]);
  
  useEffect(() => {
    // Initialize client cache
    initializeClientCache();
    setIsInitialized(true);
    
    // Set up memory monitoring
    const cleanup = setupMemoryMonitoring(() => {
      // This will be called when memory usage is high
      cleanupClientCache().catch(console.error);
    });
    
    // Load initial metrics
    fetchCacheMetrics();
    loadRecommendations();
    
    // Set up event listeners
    const unsubscribeKnowledge = cacheEventBus.subscribe('knowledgeUpdated', (payload) => {
      // Update event counter
      setCacheEvents(prev => [
        ...prev, 
        {event: `Knowledge base updated: ${payload.knowledgeId}`, count: payload.invalidatedCount}
      ].slice(-10)); // Keep only the last 10 events
    });
    
    const unsubscribeTopic = cacheEventBus.subscribe('topicUpdated', (payload) => {
      setCacheEvents(prev => [
        ...prev, 
        {event: `Topic updated: ${payload.topic}`, count: payload.invalidatedCount}
      ].slice(-10));
    });
    
    const unsubscribeLanguage = cacheEventBus.subscribe('languageUpdated', (payload) => {
      setCacheEvents(prev => [
        ...prev, 
        {event: `Language updated: ${payload.language}`, count: payload.invalidatedCount}
      ].slice(-10));
    });
    
    return () => {
      cleanup(); // Remove memory monitoring
      unsubscribeKnowledge();
      unsubscribeTopic();
      unsubscribeLanguage();
    };
  }, []);
  
  // Load optimization recommendations
  const loadRecommendations = async () => {
    try {
      const recs = await getCacheOptimizationRecommendations();
      setRecommendations(recs);
    } catch (error) {
      console.error('Error loading recommendations:', error);
    }
  };
  
  // Fetch cache metrics
  const fetchCacheMetrics = async () => {
    setIsLoadingMetrics(true);
    try {
      const allMetrics = await getAllCacheMetrics();
      
      // Calculate hit rates
      const result: Record<string, CacheMetrics> = {};
      
      for (const [store, storeMetrics] of Object.entries(allMetrics)) {
        const hitRate = await getCacheHitRate(store);
        
        result[store] = {
          ...storeMetrics,
          hitRate: hitRate !== null ? hitRate : 0
        };
      }
      
      setMetrics(result);
      
      // Also fetch size metrics
      const sizes = await getCacheSizeMetrics();
      setSizeMetrics(sizes);
    } catch (error) {
      console.error('Error fetching cache metrics:', error);
    } finally {
      setIsLoadingMetrics(false);
    }
  };
  
  const cleanupCache = async () => {
    return cleanupClientCache();
  };
  
  const clearCache = async () => {
    const result = await clearAllCaches();
    if (result) {
      // Refresh metrics after clearing cache
      fetchCacheMetrics();
    }
    return result;
  };
  
  const optimizeCache = async () => {
    await optimizeClientCache();
    // Refresh metrics after optimization
    fetchCacheMetrics();
    // Refresh recommendations
    loadRecommendations();
  };
  
  const invalidateByTopic = async (topic: string) => {
    const count = await invalidateCacheByTopic(topic);
    // Refresh metrics
    fetchCacheMetrics();
    return count;
  };
  
  const invalidateByLanguage = async (language: string) => {
    const count = await invalidateCacheByLanguage(language);
    // Refresh metrics
    fetchCacheMetrics();
    return count;
  };
  
  const invalidateByKnowledge = async (knowledgeId: string) => {
    const count = await invalidateCacheByKnowledgeUpdate(knowledgeId);
    // Refresh metrics
    fetchCacheMetrics();
    return count;
  };
  
  return {
    isInitialized,
    cleanupCache,
    clearCache,
    optimizeCache,
    fetchCacheMetrics,
    metrics,
    sizeMetrics,
    recommendations,
    isLoadingMetrics,
    invalidateByTopic,
    invalidateByLanguage,
    invalidateByKnowledge,
    deviceCapabilities,
    cacheEvents
  };
};
